/**
 * The Spread/Total board's default line (#780, #1053 Part 1). Its own module so
 * Game Detail can use the board's rule (#1071 "Game Detail follows the
 * board"): the board imports from Game Detail, so Game Detail cannot import
 * the board's index. Re-exported from `./index` for existing callers.
 */
import type { SpreadLine, TotalLine } from '../ValueDashboard/types';
import { linePriceCents } from './synthesize';

/** Index of the line whose primary venue YES priceCents is closest to 50¢, or
 *  null when NOT ONE line carries a price yet — an empty strip included.
 *
 *  #780: the two answers used to be spelled the same way (`0`), which is why
 *  a row seeded before its books were priced froze on the end of the ladder.
 *  "The first line is closest to 50¢" and "nothing is priced, ask again
 *  later" are different facts, so callers get to tell them apart. */
export function nearestToFiftyIndex(
  lines: Array<SpreadLine | TotalLine>,
): number | null {
  // g1xl-v2#1053 Part 1 — venue-aware: the default line is the KALSHI line
  // nearest even money (Kalshi is the execution venue, and its ladder is
  // fully served); a Poly-only line is the default only when no Kalshi
  // contract exists. Cold Kalshi books must not freeze on a Poly placeholder. Before this, Poly's 54-line ladder put a Poly-only line at
  // 50¢ ahead of the Kalshi main line, and the three-line window around it
  // held no Kalshi line at all.
  const pick = (eligible: (line: SpreadLine | TotalLine) => boolean): number | null => {
    let bestIdx: number | null = null;
    let bestDist = Infinity;
    for (let i = 0; i < lines.length; i++) {
      if (!eligible(lines[i])) continue;
      const cents = linePriceCents(lines[i]);
      if (cents == null) continue;
      const dist = Math.abs(cents - 50);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    }
    return bestIdx;
  };
  return lines.some(line => line.kalshi != null)
    ? pick(line => line.kalshi != null)
    : pick(() => true);
}

/** Index of the line whose primary venue YES priceCents is closest to 50¢.
 *  Determines the *default* chip when the strip first renders for a game;
 *  the user can navigate to any other line. With nothing priced the first
 *  line is all the board can show — a RENDERED fallback only, never a
 *  selection worth remembering (see `seedLineKeys`). */
export function defaultLineIndex(
  lines: Array<SpreadLine | TotalLine>,
): number {
  return nearestToFiftyIndex(lines) ?? Math.max(0, lines.findIndex(line => line.kalshi != null));
}
